import { Disclosure } from '@headlessui/react';
import classNames from 'classnames';
import React, { FC, ReactElement } from 'react';

import JourneyListbox from 'src/common/form/JourneyListbox';
import { CameraStrongIcon, ChevronUpStrongIcon, MicStrongIcon } from 'src/monet/icons';

import { Nullable } from 'src/types/nullable.type';

interface RTCMediaDevice {
  deviceId: string;
  groupId: string;
  kind: string;
  label: string;
}

interface RecordSettingsProps {
  audioInputs: Array<RTCMediaDevice>;
  videoInputs: Array<RTCMediaDevice>;
  selectedAudioInput: Nullable<RTCMediaDevice>;
  selectedVideoInput: Nullable<RTCMediaDevice>;
  onDeviceChange: (device: RTCMediaDevice) => void;
}

const RecordSettingsIcon = ({ icon }: { icon: ReactElement }) => (
  <div className='flex flex-shrink-0 w-[46px] h-[46px] bg-bedrock-brand-primary/transparent rounded-lg p-[13px]'>
    {icon}
  </div>
);

export const RecordSettings: FC<RecordSettingsProps> = ({
  audioInputs,
  videoInputs,
  selectedAudioInput,
  selectedVideoInput,
  onDeviceChange,
}) => {
  return (
    <Disclosure as='div' className='flex flex-col space-y-sm'>
      {({ open }) => (
        <>
          <Disclosure.Button className='flex items-center justify-between has-hover'>
            <div className='text-bedrock-p-strong text-bedrock-black'>Source settings</div>
            <div className='text-bedrock-black'>
              <ChevronUpStrongIcon
                className={classNames('transition-transform', {
                  '-rotate-180': !open,
                })}
              />
            </div>
          </Disclosure.Button>
          {open && (
            <Disclosure.Panel>
              <div className='flex flex-col space-y-2'>
                <div className='flex items-start space-x-2'>
                  <RecordSettingsIcon icon={<CameraStrongIcon className='text-bedrock-brand-secondary' />} />
                  <JourneyListbox
                    containerClassName='w-full min-w-0'
                    options={videoInputs}
                    defaultLabel='Select Video Source'
                    renderValue={(input: RTCMediaDevice) => input.label}
                    renderKey={(input: RTCMediaDevice) => input.deviceId}
                    selected={selectedVideoInput}
                    onChange={(value: RTCMediaDevice) => onDeviceChange(value)}
                  />
                </div>
                <div className='flex items-start space-x-2'>
                  <RecordSettingsIcon icon={<MicStrongIcon className='text-bedrock-brand-secondary' />} />
                  <JourneyListbox
                    containerClassName='w-full min-w-0'
                    options={audioInputs}
                    defaultLabel='Select Audio Source'
                    renderValue={(input: RTCMediaDevice) => input.label}
                    renderKey={(input: RTCMediaDevice) => input.deviceId}
                    selected={selectedAudioInput}
                    onChange={(value: RTCMediaDevice) => onDeviceChange(value)}
                  />
                </div>
              </div>
            </Disclosure.Panel>
          )}
        </>
      )}
    </Disclosure>
  );
};
